import { useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import {
  addPortfolioVideo,
  updatePortfolioVideo,
  removePortfolioVideo,
} from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";
import toast from "react-hot-toast";
import type { PortfolioVideoItem } from "@/redux/feature/creatorApplicationV2/creatorApplicationTypesV2";

import VideoUploaderWithPreview from "@/components/ui/VideoUploaderWithPreview";
import type { FileDetails } from "@/types/types";
import VideoPlayerV3, { DEFAULT_BLURHASH } from "@/components/ui/VideoPlayerV3";
import { Play, Check, Trash2, Pencil } from "lucide-react";
import LinkPlus from "@/assets/icons/authentication/LinkPlus.svg";

const inputCls =
  "w-full h-[30px] rounded-[8px] border border-[#EDEDED] bg-white px-2 text-[11px] text-[#2A2A2A] placeholder:text-[#959FB2] outline-none focus:border-[#FF6A00]";

const textareaCls =
  "w-full rounded-[10px] border border-[#E9E9E9] bg-white px-2 py-2.5 text-[12px] text-[#222] placeholder:text-[#959FB2] outline-none resize-none focus:border-[#FF6A00]";

const PortfolioVideosSection = () => {
  const dispatch = useAppDispatch();

  const portfolioVideos = useAppSelector(
    (s) => s.signUpV2?.v2Extras?.portfolioVideos ?? [],
  );

  // bottom edit/add form state
  const [uploadedFiles, setUploadedFiles] = useState<FileDetails[]>([]);
  const [draftLink, setDraftLink] = useState("");
  const [draftBrand, setDraftBrand] = useState("");
  const [draftPersonal, setDraftPersonal] = useState(false);
  const [draftDesc, setDraftDesc] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [playingUrl, setPlayingUrl] = useState("");

  // top upload/link preview state
  const [topUploadedFiles, setTopUploadedFiles] = useState<FileDetails[]>([]);
  const [topDraftLink, setTopDraftLink] = useState("");
  const [topLinkPreviewReady, setTopLinkPreviewReady] = useState(false);
  const addVideoFormRef = useRef<HTMLDivElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);

  const uploaded = editingId
    ? uploadedFiles[0] || null
    : topUploadedFiles[0] || null;

  const uploadReady = !!uploaded?.live_url;

  const hasUpload = editingId
    ? uploadedFiles.length > 0
    : topUploadedFiles.length > 0;

  const effectiveMode = hasUpload
    ? "upload"
    : editingId
      ? draftLink.trim()
        ? "link"
        : "upload"
      : topDraftLink.trim()
        ? "link"
        : "upload";

  const canSubmit = useMemo(() => {
    if (hasUpload) return uploadReady;

    if (effectiveMode === "link") {
      const currentLink = editingId ? draftLink.trim() : topDraftLink.trim();
      try {
        new URL(currentLink);
        return true;
      } catch {
        return false;
      }
    }

    return false;
  }, [
    hasUpload,
    uploadReady,
    effectiveMode,
    editingId,
    draftLink,
    topDraftLink,
  ]);

  const resetDraft = () => {
    setUploadedFiles([]);
    setDraftLink("");
    setDraftBrand("");
    setDraftPersonal(false);
    setDraftDesc("");
    setEditingId(null);
    setBusy(false);
  };

  const resetTopDraft = () => {
    setTopUploadedFiles([]);
    setTopDraftLink("");
    setTopLinkPreviewReady(false);
  };

  const clearTopLinkPreview = () => {
    resetTopDraft();
  };

  const handleAddMore = () => {
    resetDraft();
    resetTopDraft();

    // The composer lives above the saved cards. Move the creator back to it
    // instead of silently clearing an off-screen form.
    requestAnimationFrame(() => {
      addVideoFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      linkInputRef.current?.focus({ preventScroll: true });
    });
  };

  const startEdit = (item: PortfolioVideoItem) => {
    setEditingId(item.id);
    setDraftBrand(item.brandName ?? "");
    setDraftPersonal(!!item.isPersonalProject);
    setDraftDesc(item.description ?? "");

    if (item.sourceType === "link") {
      setDraftLink(item.linkUrl ?? "");
      setUploadedFiles([]);
    } else {
      setDraftLink("");
      setUploadedFiles(item.file ? [item.file as unknown as FileDetails] : []);
    }
  };

  const handleDelete = (id: string) => {
    dispatch(removePortfolioVideo({ id }));
    if (editingId === id) resetDraft();
  };

  const handlePrepareLinkPreview = () => {
    if (!topDraftLink.trim()) return toast.error("Please add a video link.");
    try {
      new URL(topDraftLink.trim());
      if (topUploadedFiles.length > 0) {
        setTopUploadedFiles([]);
      }
      setTopLinkPreviewReady(true);
    } catch {
      return toast.error("Please provide a valid link URL.");
    }
  };

  const handleAddOrSave = async () => {
    if (busy) return;
    const mode = effectiveMode;

    // if (mode === "upload" && !draftDesc.trim()) {
    //   return toast.error("Please add a description.");
    // }

    if (mode === "upload" && !uploadReady) {
      return toast.error("Please wait until the upload finishes.");
    }

    if (mode === "link") {
      const currentLink = editingId ? draftLink.trim() : topDraftLink.trim();
      if (!currentLink) return toast.error("Please add a video link.");
      try {
        new URL(currentLink);
      } catch {
        return toast.error("Please provide a valid link URL.");
      }
    }

    if (!hasUpload && effectiveMode !== "link") {
      return toast.error("Please upload a video or paste a link.");
    }

    setBusy(true);
    try {
      const basePatch: Partial<PortfolioVideoItem> = {
        brandName: draftBrand.trim(),
        isPersonalProject: draftPersonal,
        description: draftDesc.trim(),
      };

      if (editingId) {
        dispatch(
          updatePortfolioVideo({
            id: editingId,
            patch:
              mode === "upload"
                ? {
                    ...basePatch,
                    sourceType: "upload",
                    linkUrl: undefined,
                    file: uploaded as any,
                    uploading: false,
                    uploadError: null,
                  }
                : {
                    ...basePatch,
                    sourceType: "link",
                    linkUrl: draftLink.trim(),
                    file: undefined,
                    uploading: false,
                    uploadError: null,
                  },
          }),
        );
        toast.success("Updated!");
        resetDraft();
        return;
      }

      const id = crypto.randomUUID();
      dispatch(
        addPortfolioVideo(
          mode === "upload"
            ? ({
                id,
                ...basePatch,
                sourceType: "upload",
                file: uploaded as any,
                uploading: false,
                uploadError: null,
                createdAt: new Date().toISOString(),
              } as PortfolioVideoItem)
            : ({
                id,
                ...basePatch,
                sourceType: "link",
                linkUrl: topDraftLink.trim(),
                uploading: false,
                uploadError: null,
                createdAt: new Date().toISOString(),
              } as PortfolioVideoItem),
        ),
      );

      toast.success("Added!");
      resetDraft();
      resetTopDraft();
    } catch (e) {
      console.error(e);
      toast.error("Failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const renderTopPreview = () => {
    if (topUploadedFiles.length > 0) {
      return (
        <div className="overflow-hidden w-full">
          <VideoUploaderWithPreview
            maxFiles={1}
            useExternalState
            externalFiles={topUploadedFiles}
            setExternalFiles={setTopUploadedFiles}
            showSelectedFiles
            showUploadAnimationOnly={false}
            useLiveUrl={false}
            height="88px"
            width="120px"
            cardDesignSize="h-[88px] w-full
             md:w-[120px]"
            accept={{
              "video/mp4": [".mp4"],
              "video/quicktime": [".mov"],
              "video/webm": [],
              "video/ogg": [],
            }}
            onFilesUploaded={() => {}}
          />
        </div>
      );
    }

    if (topLinkPreviewReady && topDraftLink.trim()) {
      return (
        <div className="mb-3 h-[120px] w-[120px] rounded-[14px] bg-[#EAEAEA] p-3 flex flex-col justify-between">
          <div className="flex-1 flex items-center justify-center">
            <div className="w-full rounded-[10px] bg-white px-2 py-2 text-[10px] text-[#B1B1B1] truncate overflow-hidden whitespace-nowrap">
              {topDraftLink}
            </div>
          </div>

          <button
            type="button"
            onClick={clearTopLinkPreview}
            className="self-start inline-flex items-center gap-1 h-6 px-2 rounded-full border border-[#E9E9E9] bg-white text-[10px] text-[#111827] font-medium"
          >
            <Trash2 className="w-3 h-3 text-[#EF4444]" />
            Remove
          </button>
        </div>
      );
    }

    return (
      <div className="mb-[2px] w-full overflow-hidden">
        <VideoUploaderWithPreview
          maxFiles={1}
          useExternalState
          externalFiles={topUploadedFiles}
          setExternalFiles={setTopUploadedFiles}
          showSelectedFiles
          showUploadAnimationOnly={false}
          useLiveUrl={false}
          height="88px"
          width="100%"
          cardDesignSize="h-[88px] w-full bg-red-40
             md:w-[120px]"
          accept={{
            "video/mp4": [".mp4"],
            "video/quicktime": [".mov"],
            "video/webm": [],
            "video/ogg": [],
          }}
          onFilesUploaded={() => {}}
        />
      </div>
    );
  };

  return (
    <div className="w-full space-y-2">
      <div>
        <h6 className="text-[14px] font-medium leading-none text-[#222]">
          Upload your Portfolio videos
        </h6>
        <p className="mt-1 text-[9px] text-[#7C7C7C]">
          UGC, brand ads, reels, or speaking samples work best.
        </p>
      </div>

      <div ref={addVideoFormRef} className="rounded-[8px] bg-[#FAFAFA] p-2">
        <div className="flex gap-4 items-start max-md:flex-col">
          <div className="w-full md:w-[150px] shrink-0">
            <div className="rounded-[16px] border border-dashed border-[#E7E7E7] bg-[#FAFAFA] p-2 flex flex-col items-center">
              {renderTopPreview()}

              {!topLinkPreviewReady && topUploadedFiles.length === 0 && (
                <>
                  <span className="mb-2 text-[11px] text-[#9A9A9A]">OR</span>

                  <div className="relative w-full">
                    <input
                      ref={linkInputRef}
                      value={topDraftLink}
                      onChange={(e) => {
                        setTopDraftLink(e.target.value);
                        setTopLinkPreviewReady(false);
                        if (e.target.value && topUploadedFiles.length > 0) {
                          setTopUploadedFiles([]);
                        }
                      }}
                      placeholder="Add link"
                      className="w-full h-7 rounded-[10px] border border-[#DADADA] bg-white pl-3 pr-[50px] text-[11px] text-[#2A2A2A] placeholder:text-[#B8B8B8] placeholder:font-normal outline-none"
                    />

                    <button
                      onClick={handlePrepareLinkPreview}
                      type="button"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 px-2 rounded-[10px] border border-[#EDEDED] bg-white text-[10px] text-[#344054] font-medium flex items-center gap-x-[2px]"
                    >
                      <LinkPlus />
                      <span>preview</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0 space-y-2">
            {!editingId && (
              <>
                <input
                  type="text"
                  placeholder="Brand name"
                  value={draftBrand}
                  onChange={(e) => setDraftBrand(e.target.value)}
                  className={inputCls}
                />

                <textarea
                  rows={4}
                  placeholder="Description (Optional - not visible to clients)"
                  value={draftDesc}
                  onChange={(e) => setDraftDesc(e.target.value)}
                  className={textareaCls}
                />

                <div>
                  <button
                    type="button"
                    onClick={handleAddOrSave}
                    disabled={busy}
                    className="h-7 px-2.5 rounded-full bg-[#FF6A00] text-white text-[11px] font-medium disabled:opacity-50"
                  >
                    Add to Profile
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {portfolioVideos.length > 0 && (
        <div className="space-y-5">
          {!editingId && (
            <button
              type="button"
              onClick={handleAddMore}
              className="text-[#FF6A00] text-[10px] font-medium border border-dashed border-[#EDEDED] px-2.5 py-0.5 rounded-full"
            >
              +Add More Videos
            </button>
          )}

          {portfolioVideos.map((v, idx) => {
            const url =
              v.sourceType === "upload"
                ? (v.file?.live_url as string | undefined)
                : (v.linkUrl as string | undefined);

            const thumb =
              v.sourceType === "upload" ? (v.file?.thumbnail as string) : "";

            const hash =
              v.sourceType === "upload"
                ? (v.file?.hash as string) || DEFAULT_BLURHASH
                : DEFAULT_BLURHASH;

            const isEditing = editingId === v.id;

            return (
              <div key={v.id} className="space-y-1">
                <div className="rounded-[18px] border border-[#ECECEC] bg-white p-3">
                  <div className="flex gap-3 items-start">
                    <div className="w-[104px] h-[104px] rounded-[14px] bg-[#EEEEEE] overflow-hidden shrink-0 flex items-center justify-center relative">
                      {isEditing &&
                      v.sourceType === "upload" &&
                      uploadedFiles.length === 0 ? (
                        <VideoUploaderWithPreview
                          maxFiles={1}
                          useExternalState
                          externalFiles={uploadedFiles}
                          setExternalFiles={setUploadedFiles}
                          showSelectedFiles
                          showUploadAnimationOnly={false}
                          useLiveUrl={false}
                          height="88px"
                          width="104px"
                          cardDesignSize="h-[88px] w-[88px]"
                          accept={{
                            "video/mp4": [".mp4"],
                            "video/quicktime": [".mov"],
                            "video/webm": [],
                            "video/ogg": [],
                          }}
                          onFilesUploaded={() => {}}
                        />
                      ) : v.sourceType === "upload" && url ? (
                        <VideoPlayerV3
                          url={url}
                          thumbnailUrl={thumb}
                          playing={playingUrl === url}
                          muted={false}
                          blurhash={hash}
                          onPlay={() =>
                            setPlayingUrl((cur) => (cur === url ? "" : url))
                          }
                          border="rounded-[14px]"
                        />
                      ) : v.sourceType === "link" ? (
                        <div className="w-full h-full bg-[#EAEAEA] p-3 flex flex-col justify-between">
                          <div className="flex-1 flex items-center justify-center">
                            <div className="w-full rounded-[10px] bg-white px-2 py-2 text-[10px] text-[#B1B1B1] truncate overflow-hidden whitespace-nowrap">
                              {isEditing ? draftLink : v.linkUrl}
                            </div>
                          </div>

                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => {
                                setDraftLink("");
                                setUploadedFiles([]);
                              }}
                              className="self-start inline-flex items-center gap-1 h-6 px-2 rounded-full border border-[#E9E9E9] bg-white text-[10px] text-[#111827] font-medium"
                            >
                              <Trash2 className="w-3 h-3 text-[#EF4444]" />
                              Remove
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm">
                          <Play className="w-4 h-4 fill-[#777] text-[#777] ml-0.5" />
                        </div>
                      )}

                      {!url && v.sourceType !== "link" && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm">
                            <Play className="w-4 h-4 fill-[#777] text-[#777] ml-0.5" />
                          </div>
                        </div>
                      )}
                    </div>

                    {isEditing &&
                      v.sourceType === "upload" &&
                      uploadedFiles.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setUploadedFiles([])}
                          className="-mt-1 text-[10px] font-medium text-[#FF6A00]"
                        >
                          Replace video
                        </button>
                      )}

                    <div className="flex-1 min-w-0 space-y-2">
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            placeholder="Brand name"
                            value={draftBrand}
                            onChange={(e) => setDraftBrand(e.target.value)}
                            className={inputCls}
                          />

                          <textarea
                            rows={4}
                            placeholder="What was this video for and what was your role?"
                            value={draftDesc}
                            onChange={(e) => setDraftDesc(e.target.value)}
                            className={textareaCls}
                          />

                          <div className="flex items-center gap-3 pt-0.5">
                            <button
                              type="button"
                              onClick={handleAddOrSave}
                              disabled={busy || !canSubmit}
                              className="h-8 px-4 rounded-full bg-[#FF6A00] text-white text-[12px] font-semibold disabled:opacity-50"
                            >
                              Save Changes
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(v.id)}
                              className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-[#E9E9E9] bg-white text-[12px] text-[#E45A5A]"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete
                            </button>

                            <button
                              type="button"
                              onClick={resetDraft}
                              className="text-[12px] text-[#888]"
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-[12px] font-medium text-[#333] leading-none">
                            {v.brandName || "Untitled"}
                          </div>

                          <div className="rounded-[10px] border border-[#EFEFEF] bg-white px-2 py-1 min-h-[60px]">
                            <p className="text-[11px] leading-[1.7] text-[#565656]">
                              {v.description || "No description added yet."}
                            </p>
                          </div>

                          <div className="flex items-center flex-wrap gap-3 pt-0.5">
                            {idx === 0 ? (
                              <span className="inline-flex items-center gap-1 text-[11px] text-[#43B649] font-medium border border-[#EDEDED] p-1.5 rounded-full">
                                <span className="w-4 h-4 rounded-full bg-[#43B649] flex items-center justify-center">
                                  <Check className="w-3 h-3 text-white" />
                                </span>
                                <span className="text-[#FF6600]">Added</span>
                              </span>
                            ) : null}

                            <button
                              type="button"
                              onClick={() => startEdit(v)}
                              className="inline-flex items-center gap-1 h-7 px-2 rounded-full border border-[#DDDDDD] bg-white text-[11px] text-[#555]"
                            >
                              <Pencil className="w-3 h-3" />
                              Modify
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(v.id)}
                              className="ml-auto inline-flex items-center gap-1 h-8 px-3 rounded-full border border-[#E9E9E9] bg-white text-[12px] text-[#E45A5A]"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PortfolioVideosSection;
